import {
    Box, styled, Typography, Popper, Paper, Fade, Skeleton,
} from '@mui/material';
import { useState } from 'react';
import { useResolvedAssetUrl } from 'src/hooks/useResolvedAssetUrl';
import { LoadingStateImage } from './LoadingStateImage';

export type DpcImage = {
    previewUrl?: string;
    originalSourceUrl?: string;
    purpose?: string;
};

type AssetsImagesProps = {
    images: DpcImage[];
};

const StyledGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: '100%',
}));

const IMAGE_LOAD_RETRIES = 2;
const ENLARGED_SIZE = 400;

type HoverHandlers = {
    onEnter: (element: HTMLElement, src?: string) => void;
    onLeave: () => void;
};

const AssetThumbnail = ({
    label, src, hover,
}: { label: string; src?: string; hover: HoverHandlers }): JSX.Element => (
    <Box sx={{ p: 2 }}>
        <Typography color="text.secondary" sx={{ mb: 1, display: 'block' }} variant="caption">
            {label}
        </Typography>
        <Box
            sx={{ cursor: src ? 'zoom-in' : 'default' }}
            onMouseEnter={(e): void => hover.onEnter(e.currentTarget as HTMLElement, src)}
            onMouseLeave={hover.onLeave}
        >
            <LoadingStateImage
                maxRetries={IMAGE_LOAD_RETRIES}
                skeletonHeight={150}
                skeletonWidth={200}
                src={src}
            />
        </Box>
    </Box>
);

const OriginalAssetThumbnail = ({
    originalSourceUrl, hover,
}: { originalSourceUrl?: string; hover: HoverHandlers }): JSX.Element => {
    const { data: resolvedUrl, isLoading } = useResolvedAssetUrl(originalSourceUrl);

    if (originalSourceUrl && isLoading) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="text.secondary" sx={{ mb: 1, display: 'block' }} variant="caption">
                    Original
                </Typography>
                <Skeleton animation="wave" height={150} sx={{ borderRadius: 1 }} variant="rectangular" width={200} />
            </Box>
        );
    }

    return <AssetThumbnail hover={hover} label="Original" src={resolvedUrl} />;
};

export const AssetsImages = ({ images }: AssetsImagesProps): JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [hoverSrc, setHoverSrc] = useState<string | undefined>(undefined);
    const open = Boolean(anchorEl) && !!hoverSrc;

    const hover: HoverHandlers = {
        onEnter: (element, src): void => {
            if (!src) return;
            setAnchorEl(element);
            setHoverSrc(src);
        },
        onLeave: (): void => {
            setAnchorEl(null);
            setHoverSrc(undefined);
        },
    };

    const renderableImages = images.filter((img) => img.previewUrl || img.originalSourceUrl);

    if (renderableImages.length === 0) {
        return (
            <Box sx={{
                p: 2, textAlign: 'center', color: 'text.secondary', maxWidth: '100%',
            }}
            >
                No images to show.
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {renderableImages.map((img) => (
                <Box key={img.previewUrl ?? img.originalSourceUrl}>
                    {img.purpose && (
                        <Typography color="text.secondary" variant="overline">
                            {img.purpose}
                        </Typography>
                    )}
                    <StyledGrid>
                        <AssetThumbnail hover={hover} label="Preview" src={img.previewUrl} />
                        <OriginalAssetThumbnail hover={hover} originalSourceUrl={img.originalSourceUrl} />
                    </StyledGrid>
                </Box>
            ))}
            <Popper
                transition
                anchorEl={anchorEl}
                modifiers={[
                    { name: 'offset', options: { offset: [0, 10] } },
                    { name: 'preventOverflow', options: { altAxis: true, tether: true, padding: 16 } },
                ]}
                open={open}
                placement="right-start"
                sx={{ zIndex: 1500 }}
            >
                {({ TransitionProps }): JSX.Element => (
                    <Fade {...TransitionProps} timeout={200}>
                        <Paper
                            elevation={8}
                            sx={{
                                p: 1,
                                borderRadius: 1,
                                maxWidth: ENLARGED_SIZE + 16,
                                maxHeight: ENLARGED_SIZE + 16,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Box
                                component="img"
                                src={hoverSrc}
                                sx={{
                                    width: ENLARGED_SIZE,
                                    height: ENLARGED_SIZE,
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </Box>
    );
};
